import { NgClass } from '@angular/common'
import { Component, computed, effect, input, output } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms'
import { BasicInputComponent } from '@app/v2/shared/components/inputs/basic-input/basic-input.component'
import { BasicInputMode } from '@app/v2/shared/enums/input-modes.enum'
import { Zone, ZoneCardErrors } from '../../../types/zone.type'

@Component({
  selector: 'app-zone-card',
  standalone: true,
  imports: [NgClass, ReactiveFormsModule, BasicInputComponent],
  templateUrl: './zone-card.component.html',
  styleUrl: './zone-card.component.scss'
})
export class ZoneCardComponent {
  public readonly zone = input<Zone>({ id: 0, name: '', max_capacity: 0, weight: 1 })
  public readonly errors = input<ZoneCardErrors | undefined>(undefined)
  public readonly isDragging = input<boolean>(false)
  public readonly isHighlighted = input<boolean>(false)
  public readonly isFocused = input<boolean>(false)
  public readonly nameChanged = output<{ zone: Zone; name: string }>()
  public readonly nameBlurred = output<{ zone: Zone; name: string }>()
  public readonly focusChanged = output<{ zone: Zone; focused: boolean }>()
  public readonly capacityChanged = output<{ zone: Zone; max_capacity: number | null }>()
  public readonly deleteClicked = output<Zone>()

  public readonly nameControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.maxLength(191), (control) => (String(control.value ?? '').trim().length ? null : { required: true })]
  })
  public readonly capacityControl = new FormControl<number | null>(null)

  public readonly isDraft = computed(() => (this.zone()?.id ?? 0) <= 0)
  public readonly BasicInputMode = BasicInputMode

  private readonly _nameValue = toSignal(this.nameControl.valueChanges, { initialValue: this.nameControl.value })
  private readonly _capacityValue = toSignal(this.capacityControl.valueChanges, { initialValue: this.capacityControl.value })
  private _didInitNameEffect = false
  private _didInitCapacityEffect = false

  constructor() {
    effect(() => {
      this._syncControlsFromZone(this.zone())
    })

    effect(() => {
      this._syncServerErrors(this.errors())
    })

    effect(() => {
      const value = this._nameValue()
      if (!this._didInitNameEffect) {
        this._didInitNameEffect = true
        return
      }
      const nextName = value ?? ''
      if ((this.zone()?.name ?? '') === nextName) return
      this._clearServerError(this.nameControl)
      this.nameChanged.emit({ zone: this.zone(), name: nextName })
    })

    effect(() => {
      const value = this._capacityValue()
      if (!this._didInitCapacityEffect) {
        this._didInitCapacityEffect = true
        return
      }
      const nextCapacity = this._parseCapacity(value)
      if ((this.zone()?.max_capacity ?? 0) === nextCapacity) return
      this._clearServerError(this.capacityControl)
      this.capacityChanged.emit({ zone: this.zone(), max_capacity: nextCapacity })
    })
  }

  /**
   * Block any non-digit input on the capacity field so the text input behaves like a number-only field.
   * Allows navigation/edit keys and modifier combinations (copy/paste/select all) so the form stays usable.
   */
  public onCapacityKeydown(event: KeyboardEvent): void {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']
    if (allowedKeys.includes(event.key)) return
    if (event.ctrlKey || event.metaKey || event.altKey) return
    if (!/^[0-9]$/.test(event.key)) event.preventDefault()
  }

  /**
   * Emit the delete request to the parent. The parent decides whether to confirm via a modal
   * (persisted zones) or remove locally (drafts).
   * @param {Event} event - The event.
   * @param {boolean} shouldDelete - Whether to delete the zone.
   */
  public onDeleteClicked(event?: Event, shouldDelete: boolean = true): void {
    event?.stopPropagation()
    if (!shouldDelete) {
      if (this._isDesktopMousePress(event)) this.focusChanged.emit({ zone: this.zone(), focused: false })
      return
    }
    this.focusChanged.emit({ zone: this.zone(), focused: false })
    this.deleteClicked.emit(this.zone())
  }

  /**
   * Check if the event is a desktop mouse press.
   * @param {Event} event - The event.
   * @returns {boolean}
   */
  private _isDesktopMousePress(event?: Event): boolean {
    if (!event) return false
    if (typeof PointerEvent !== 'undefined' && event instanceof PointerEvent) return event.pointerType === 'mouse'
    return event instanceof MouseEvent
  }

  /**
   * Emit the name blurred event to the parent.
   * @param {FocusEvent} _event - The focus event.
   */
  public onNameBlur(_event: FocusEvent): void {
    this.nameBlurred.emit({ zone: this.zone(), name: this.nameControl.value ?? '' })
  }

  /**
   * Emit the focus changed event to the parent.
   * @returns void
   */
  public onFieldFocus(): void {
    this.focusChanged.emit({ zone: this.zone(), focused: true })
  }

  /**
   * Emit the focus changed event to the parent.
   * @returns void
   */
  public onFieldBlur(): void {
    this.focusChanged.emit({ zone: this.zone(), focused: false })
  }
  /**
   * Apply or clear a server-side error on a form control while preserving any client-side validators.
   * `basic-input.getErrorMessage()` reads the `serverError` key directly so the message renders inline.
   */
  private _applyServerError(control: FormControl, message: string | undefined | null): void {
    if (message) {
      const next = { ...(control.errors ?? {}), serverError: message }
      control.setErrors(next)
      control.markAsTouched()
    } else {
      this._clearServerError(control)
    }
  }

  /**
   * Remove a `serverError` from a control without touching other validation errors.
   */
  private _clearServerError(control: FormControl): void {
    if (!control.errors?.['serverError']) return
    const { serverError: _serverError, ...rest } = control.errors
    control.setErrors(Object.keys(rest).length ? rest : null)
  }

  /**
   * Sync the controls from the zone.
   * @param {Zone} zone - The zone to sync from.
   */
  private _syncControlsFromZone(zone: Zone): void {
    this.nameControl.setValue(zone.name ?? '', { emitEvent: false })
    this.capacityControl.setValue(zone.max_capacity ?? null, { emitEvent: false })
  }

  /**
   * Sync the server errors to the controls.
   * @param {ZoneCardErrors | undefined} errors - The errors to sync.
   */
  private _syncServerErrors(errors: ZoneCardErrors | undefined): void {
    this._applyServerError(this.nameControl, errors?.name)
    this._applyServerError(this.capacityControl, errors?.max_capacity)
  }

  /**
   * Parse the capacity value.
   * @param {number | null} value - The value to parse.
   * @returns {number} - The parsed capacity.
   */
  private _parseCapacity(value: number | null): number | null {
    if (!value) return null
    const parsed = Number(value)
    return Number.isNaN(parsed) ? null : parsed
  }
}
